import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface TemplateRequirement {
  template_type: string;
  required_entities: ('user' | 'business' | 'service_request' | 'review' | 'conversation')[];
  required_fields: string[];
  optional_fields: string[];
  data_sources: {
    users?: { fields: string[] };
    businesses?: { fields: string[] };
    service_requests?: { fields: string[] };
    reviews?: { fields: string[] };
    conversations?: { fields: string[] };
  };
}

export interface TemplateRequirementsResponse {
  success: boolean;
  template_type?: string;
  requirements?: TemplateRequirement;
  required_entities?: string[];
  required_fields?: string[];
  data_source_fields?: {
    users: string[];
    businesses: string[];
    service_requests: string[];
    reviews: string[];
    conversations: string[];
  };
  all_requirements?: Record<string, TemplateRequirement>;
}

export function useTemplateRequirements(templateType?: string) {
  const [requirements, setRequirements] = useState<TemplateRequirement | null>(null);
  const [allRequirements, setAllRequirements] = useState<Record<string, TemplateRequirement> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequirements = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Build the function name with query parameters for GET requests
        const functionName = templateType 
          ? `get-template-requirements?template_type=${encodeURIComponent(templateType)}`
          : 'get-template-requirements';
          
        const { data, error: functionError } = await supabase.functions.invoke(functionName, {
          method: 'GET'
        });

        if (functionError) {
          throw functionError;
        }

        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch template requirements');
        }

        if (templateType) {
          setRequirements(data.requirements);
        } else {
          setAllRequirements(data.all_requirements);
        }
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch template requirements';
        setError(errorMessage);
        console.error('Template requirements error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequirements();
  }, [templateType]);

  return {
    requirements,
    allRequirements,
    isLoading,
    error
  };
}

export function getRequiredEntities(templateType: string, allRequirements?: Record<string, TemplateRequirement>): string[] {
  if (!allRequirements || !allRequirements[templateType]) {
    return [];
  }
  return allRequirements[templateType].required_entities;
}

export function getDataSourceFields(
  templateType: string, 
  entityType: string,
  allRequirements?: Record<string, TemplateRequirement>
): string[] {
  if (!allRequirements || !allRequirements[templateType]) {
    return [];
  }
  
  const requirements = allRequirements[templateType];
  const dataSource = requirements.data_sources[entityType as keyof typeof requirements.data_sources];
  return dataSource?.fields || [];
}